"use client";

import { useTranslations } from "@/hooks/use-translations";
import {
  Card,
  CardBody,
  CardHeader,
  Chip,
  Tooltip
} from "@heroui/react";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";


export default function Coworking() {
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);
  const t = useTranslations();

  if (!t) return null;

  const featureData = [
    {
      id: "flexible-workspace",
      title: t.coworking.flexible.title,
      description: t.coworking.flexible.description,
      features: t.coworking.flexible.features,
      icon: "/assets/window-blinds.png"
    },
    {
      id: "fair-reservation-system",
      title: t.coworking.fair.title,
      description: t.coworking.fair.description,
      features: t.coworking.fair.features,
      icon: "/assets/star.png"
    },
    {
      id: "dynamic-community",
      title: t.coworking.community.title,
      description: t.coworking.community.description,
      features: t.coworking.community.features,
      icon: "/assets/charts.png"
    }
  ];

  return (
    <section id="coworking" className="flex flex-col gap-12 w-full min-h-[600px]">
      <div className="text-center flex flex-col gap-3 items-center">
        <h3 className="font-bold mb-4 text-4xl desktop:text-5xl">
          {t.coworking.title}
        </h3>
        <h4 className="text-foreground/75 text-lg desktop:text-xl max-w-3xl mx-auto">
          {t.coworking.subtitle}
        </h4>
      </div>

      <div className="flex flex-wrap justify-center items-center gap-4 max-w-5xl mx-auto">
        {featureData.map((feature) => (
          <FeatureTag
            key={feature.id}
            tag={feature.title}
            description={feature.description}
            isActive={activeFeature === feature.id}
            onHover={() => setHoveredFeature(feature.id)}
            onLeave={() => setHoveredFeature(null)}
            onClick={() => setActiveFeature(activeFeature === feature.id ? null : feature.id)}
          />
        ))}
      </div>

      {/* Feature Cards */}
      <div className="flex flex-wrap justify-center items-stretch gap-6">
        {featureData.map((feature) => {
          const isHighlighted = activeFeature === feature.id || hoveredFeature === feature.id;
          return (
            <div
              key={feature.id}
              className={`transition-opacity duration-300 ease-out ${isHighlighted
                ? 'opacity-100 z-10'
                : activeFeature || hoveredFeature
                  ? 'opacity-60'
                  : 'opacity-100'
                }`}
            >
              <FeatureCard
                title={feature.title}
                description={feature.description}
                features={feature.features}
                icon={feature.icon}
                showBookingLink={feature.id === "flexible-workspace"}
              />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function FeatureTag({
  tag,
  description,
  isActive,
  onHover,
  onLeave,
  onClick,
}: {
  tag: React.ReactNode;
  description: React.ReactNode;
  isActive: boolean;
  onHover: () => void;
  onLeave: () => void;
  onClick: () => void;
}) {
  return (
    <Tooltip
      closeDelay={0}
      content={
        <p className="text-sm text-gray-600 dark:text-gray-300 max-w-sm truncate whitespace-normal text-center">
          {description}
        </p>
      }
    >
      <Chip
        className={`tracking-wide p-2 font-light text-sm whitespace-nowrap cursor-pointer transition-colors duration-200`}
        variant="dot"
        color={isActive ? "secondary" : "primary"}
        onMouseEnter={onHover}
        onMouseLeave={onLeave}
        onClick={onClick}
      >
        {tag}
      </Chip>
    </Tooltip>
  );
}

function FeatureCard({
  title,
  description,
  features,
  icon,
  showBookingLink = false,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
  features?: string[];
  icon: string;
  showBookingLink?: boolean;
}) {
  return (
    <Card className={`p-2 w-[250px] h-full flex flex-col transition-all duration-300`}>
      <CardHeader className="flex flex-col items-start gap-4">
        <Image src={icon} alt={icon} className="w-10 h-10" width={40} height={40} loading="lazy" sizes="40px" />
        <h2 className="text-xl font-semibold">
          {title}
        </h2>
      </CardHeader>
      <CardBody className="flex flex-col flex-1">
        <p className="text-foreground/90 leading-relaxed flex-1 mb-4">
          {description}
        </p>
        {showBookingLink && (
          <Link
            href="/come-funziona-il-sistema-di-prenotazione"
            className="text-xs text-primary hover:text-primary-600 underline mb-6 inline-block"
          >
            Come funziona il sistema di prenotazione →
          </Link>
        )}
        {features && (
          <ul className="space-y-2">
            {features.map((feature, index) => (
              <li
                key={index}
                className="flex items-center text-sm text-foreground/60"
              >
                <span className="text-success mr-2">✓</span>
                {feature}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
